import { Injectable } from '@angular/core';
import { DynamicField } from 'projects/kesher-site/src/app/kesher/models/dynamic-field.model';
import * as XLSX from 'xlsx';

@Injectable({
  providedIn: 'root'
})
export class ExcelService {


  constructor() { }
  readExcelFile(
    file: File,
    sheetName: string,
    columnNames: any[],
    headerRow: number = 0,
    headerRows: number = 1,
    allowedDynamicFields: DynamicField[] = []
  ): Promise<any[]> {
    return new Promise((resolve, reject) => {
      const fileReader = new FileReader();
      const resultList: any[] = [];

      fileReader.onload = (e) => {
        try {
          const data = new Uint8Array(e.target.result as ArrayBuffer);
          const workbook = XLSX.read(data, {
            type: 'array',
            cellDates: true,
            codepage: 1255
          });

          const worksheet: XLSX.WorkSheet = workbook.Sheets[sheetName];
          if (!worksheet) return reject("שם גליון שגוי");

          const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });

          const headerRowData = (jsonData[headerRow - 1] as any[]).map((h: any) => h?.trim?.() ?? "");
          for (let i = headerRow; i < headerRows; i++) {
            for (let j = 0; j < headerRowData.length; j++) {
              const extra = jsonData[i][j]?.trim?.() ?? "";
              headerRowData[j] = `${headerRowData[j]} ${extra}`.trim();
            }
          }

          const trimmedHeaderRowData = headerRowData.map((item: any) => item.trim());
          const columnIndexes: number[] = columnNames.map(cn => trimmedHeaderRowData.indexOf(cn.name));

          // זיהוי עמודות דינאמיות חוקיות
          const dynamicFieldIndexes: { [key: string]: { index: number, fieldId: string } } = {};

          trimmedHeaderRowData.forEach((header, index) => {
            const isKnown = columnNames.some(cn => cn.name === header);
            const fieldMeta = allowedDynamicFields.find(f => f.name === header);
            if (!isKnown && fieldMeta) {
              dynamicFieldIndexes[header] = {
                index: index,
                fieldId: fieldMeta.id
              };
            }
          });
          if (jsonData.length <= headerRow + headerRows - 1)
            return reject("אין רשומות בקובץ");

          for (let i = headerRow + headerRows - 1; i < jsonData.length; i++) {
            const row = jsonData[i];
            const resultItem: any = {};

            // שדות קבועים
            for (let j = 0; j < columnIndexes.length; j++) {
              const columnIndex = columnIndexes[j];
              const columnName = columnNames[j].value;
              const columnType = columnNames[j].type;

              resultItem[columnName] = this.convertCellValue(row[columnIndex], columnType);
            }

            // שדות דינאמיים
            resultItem.dynamicFields = [];

            for (const [fieldName, { index, fieldId }] of Object.entries(dynamicFieldIndexes)) {
              resultItem.dynamicFields.push({
                fieldId: fieldId,
                fieldName: fieldName,
                value: String(row[index] ?? "")
              });
            }

            resultList.push(resultItem);
          }

          resolve(resultList);
        } catch (error) {
          reject(error);
        }
      };

      fileReader.onerror = (error) => reject(error);
      fileReader.readAsArrayBuffer(file);
    });
  }
  private parseDateValue(excelDateValue: any): Date {
    if (!excelDateValue) return null;

    if (excelDateValue instanceof Date) {
      return new Date(
        excelDateValue.getFullYear(),
        excelDateValue.getDate() - 1,
        excelDateValue.getMonth() + 1,
        12
      );
    }

    const normalizedDate = excelDateValue.toString().replace(/[-.]/g, '/');
    const parts = normalizedDate.split('/');

    const [day, month, year] = parts.length > 1
      ? parts.map(p => parseInt(p, 10))
      : [
        parseInt(normalizedDate.slice(0, 2), 10),
        parseInt(normalizedDate.slice(2, 4), 10),
        parseInt(normalizedDate.slice(4, 6), 10)
      ];
    const date = new Date(year, month - 1, day, 12);
    return isNaN(date.getTime()) ? null : date;
  }

  private convertCellValue(value: any, type: string): any {
    const converters = {
      string: (val: any) => val?.toString(),
      tel: (val: any) => this.addingPrefix0IfNone(val),
      int: (val: any) => val ? Number(val) : 0,
      date: (val: any) => this.parseDateValue(val)
    };

    return (converters[type] || ((val: any) => val))(value) ?? null;
  }

  addingPrefix0IfNone(value: any): any {
    if (value == null)
      return value
    if (value?.toString().charAt(0) == '0')
      return value?.toString();
    else return "0" + value?.toString()
  }

  createExcelFile(data: any): Promise<Blob> {
    return new Promise((resolve, reject) => {
      const workbook = { Workbook: { Views: [{ RTL: true }] }, Sheets: {}, SheetNames: [] }
      const worksheet = XLSX.utils.aoa_to_sheet(data);

      //const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet);

      // Generate the Excel file and trigger the file download
      const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
      resolve(new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }));
    });
  }

  isUTF8(arrayBuffer: ArrayBuffer): boolean {
    const uint8Array = new Uint8Array(arrayBuffer);
    try {
      // Attempt to decode the Uint8Array as UTF-8
      const text = new TextDecoder('utf-8', { fatal: true }).decode(uint8Array);
      return true; // If decoding succeeds, it is valid UTF-8
    } catch (e) {
      return false; // If an error occurs, it is not valid UTF-8
    }
  }
}
